/**
 * useSearchBar — headless React hook for the FAB SearchBar.
 *
 * Owns query state, debouncing, async fetch, ranked-hit list, and keyboard-
 * navigation index. Returns plain props the consumer wires to inputs and
 * lists. The visual `<SearchBar>` component uses this hook; consumers may
 * also use it directly to render a custom UI.
 *
 * Per E-023 §H — "Headless variant exposed too — apps can render their own
 * UI on top of the same hook."
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
export function useSearchBar(opts) {
    const [query, setQueryRaw] = useState("");
    const [hits, setHits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const debounceMs = opts.debounceMs ?? 200;
    const timerRef = useRef(null);
    const reqIdRef = useRef(0);
    const setQuery = useCallback((q) => {
        setQueryRaw(q);
    }, []);
    const reset = useCallback(() => {
        setQueryRaw("");
        setHits([]);
        setError(null);
        setHighlightedIndex(-1);
        setLoading(false);
    }, []);
    // Debounced fetch.
    useEffect(() => {
        if (timerRef.current)
            clearTimeout(timerRef.current);
        if (query.trim() === "") {
            setHits([]);
            setHighlightedIndex(-1);
            setLoading(false);
            setError(null);
            return;
        }
        const myReqId = ++reqIdRef.current;
        setLoading(true);
        timerRef.current = setTimeout(async () => {
            try {
                const result = await opts.searchFn(query, opts.ctx, {
                    mount: opts.mount,
                    limit: opts.limit,
                });
                // Drop stale responses (a newer request was issued meanwhile).
                if (myReqId !== reqIdRef.current)
                    return;
                setHits(result);
                setHighlightedIndex(result.length > 0 ? 0 : -1);
                setError(null);
            }
            catch (err) {
                if (myReqId !== reqIdRef.current)
                    return;
                setError(err instanceof Error ? err.message : String(err));
                setHits([]);
                setHighlightedIndex(-1);
            }
            finally {
                if (myReqId === reqIdRef.current)
                    setLoading(false);
            }
        }, debounceMs);
        return () => {
            if (timerRef.current)
                clearTimeout(timerRef.current);
        };
    }, [query, opts.ctx, opts.searchFn, opts.mount, opts.limit, debounceMs]);
    const next = useCallback(() => {
        setHighlightedIndex((i) => {
            if (hits.length === 0)
                return -1;
            if (i < 0)
                return 0;
            return Math.min(i + 1, hits.length - 1);
        });
    }, [hits.length]);
    const prev = useCallback(() => {
        setHighlightedIndex((i) => {
            if (hits.length === 0)
                return -1;
            if (i <= 0)
                return 0;
            return i - 1;
        });
    }, [hits.length]);
    const active = useMemo(() => highlightedIndex >= 0 && highlightedIndex < hits.length
        ? hits[highlightedIndex]
        : null, [hits, highlightedIndex]);
    return {
        query,
        setQuery,
        hits,
        loading,
        error,
        highlightedIndex,
        setHighlightedIndex,
        next,
        prev,
        active,
        reset,
    };
}
//# sourceMappingURL=useSearchBar.js.map